import {
    AccessTypeT,
    ScopeBlockT,
    ScopeT,
    SelectionBaseT,
    UserScopeT,
} from 'business/definitions';
import { SCOPES, WRITE_SCOPES } from 'business/constants';
import { UserCreationFormState } from './types';

export type ScopeType = typeof SCOPES | typeof WRITE_SCOPES;

/**
 * Removes the scope from the new user when the access type is set to no access.
 * @param {UserCreationFormState} state - The current state of the user creation form
 * @param {ScopeT} scope - The scope to remove from the new user
 * @param {ScopeType} scopeKey - The key to specify which list of scopes to update
 * @returns {UserCreationFormState} - The new state of the user creation form
 */
export const removeScope = (
    state: UserCreationFormState,
    scope: ScopeT,
    scopeKey: ScopeType = SCOPES,
): UserCreationFormState => ({
    ...state,
    [scopeKey]: state[scopeKey].filter(
        (userScope) => userScope.name !== scope.name,
    ),
});

/** Updates the user scope with a given block value
 * @param {UserCreationFormState} state - The current state of the user creation form
 * @param {ScopeT} scope - The scope to add or update from the new user
 * @param {ScopeBlockT[]} blocks - The blocks to update for the scope
 * @param {ScopeType} key - The key to specify which list of scopes to update
 * @returns {UserCreationFormState} - The new state of the user creation form
 */
const updateScope = (
    state: UserCreationFormState,
    scope: ScopeT,
    accessType: AccessTypeT,
    scopeKey: ScopeType,
    blocks: ScopeBlockT[] = [],
): UserCreationFormState => {
    const otherUserScopes = state[scopeKey].filter(
        (userScope) => userScope.name !== scope.name,
    );
    const newUserScope: UserScopeT = {
        name: scope.name,
        accessType,
        blocks,
    };
    return {
        ...state,
        [scopeKey]: [...otherUserScopes, newUserScope],
    };
};

/**
 * Adds or updates the scope of the new user
 * @param {UserCreationFormState} state - The current state of the user creation form
 * @param {ScopeT} scope - The scope to add or update from the new user
 * @param {ScopeType} scopeKey - The key to specify which list of scopes to update
 * @returns {UserCreationFormState} - The new state of the user creation form
 */
export const updateScopeAccessType = (
    state: UserCreationFormState,
    scope: ScopeT,
    scopeKey: ScopeType = SCOPES,
): UserCreationFormState => {
    if (!scope.accessType) {
        return state;
    }
    return updateScope(state, scope, scope.accessType, scopeKey);
};

/**
 * Updates the access type of the scope of the new user
 * @param {UserCreationFormState} state - The current state of the user creation form
 * @param {ScopeT} scope - The scope configuration
 * @param {string} blockName - The name of the restriction block to update
 * @param {SelectionBaseT[]} blockValues - The new values of the restriction block
 * @returns {UserCreationFormState} - The new state of the user creation form
 * @param {ScopeType} scopeKey - The key to specify which list of scopes to update
 */
export const updateScopeBlockValues = (
    state: UserCreationFormState,
    scope: ScopeT,
    blockName: string,
    blockValues: SelectionBaseT[],
    scopeKey: ScopeType = SCOPES,
): UserCreationFormState => {
    const currentUserScope = state[scopeKey].find(
        (userScope) => userScope.name === scope.name,
    );
    if (!currentUserScope) return state;

    const otherBlocks = currentUserScope.blocks.filter(
        (userScopeBlock) => userScopeBlock.name !== blockName,
    );

    if (!blockValues.length) {
        return updateScope(
            state,
            scope,
            currentUserScope.accessType,
            scopeKey,
            otherBlocks,
        );
    }
    const newBlock: ScopeBlockT = {
        name: blockName,
        values: blockValues.map(
            (blockValue) => blockValue.key || blockValue.value,
        ),
    };

    return updateScope(state, scope, currentUserScope.accessType, scopeKey, [
        ...otherBlocks,
        newBlock,
    ]);
};

/**
 * Remove a single block value from the user scope.
 * @param {UserCreationFormState} state - The current state of the user creation form
 * @param {ScopeT} scope - The scope configuration
 * @param {string} blockName - The name of the restriction block to update
 * @param {SelectionBaseT} blockValue - The value to remove from the restriction block
 * @param {ScopeType} scopeKey - The key to specify in which list of scopes consumer needs to remove the value
 * @returns {UserCreationFormState} - The new state of the user creation form
 */
export const removeBlockValueFromScope = (
    state: UserCreationFormState,
    scope: ScopeT,
    blockName: string,
    blockValue: SelectionBaseT,
    scopeKey: ScopeType = SCOPES,
): UserCreationFormState => {
    const currentUserScope = state[scopeKey].find(
        (userScope) => userScope.name === scope.name,
    );
    if (!currentUserScope) return state;

    const otherBlocks = currentUserScope.blocks.filter(
        (userScopeBlock) => userScopeBlock.name !== blockName,
    );
    const currentBlock = currentUserScope.blocks.find(
        (userScopeBlock) => userScopeBlock.name === blockName,
    );
    if (!currentBlock) return state;

    const newBlock: ScopeBlockT = {
        ...currentBlock,
        values: currentBlock.values.filter(
            (value) => value !== blockValue.value,
        ),
    };

    // If the block is empty, we remove it from the scope
    if (!newBlock.values.length) {
        return updateScope(
            state,
            scope,
            currentUserScope.accessType,
            scopeKey,
            otherBlocks,
        );
    }

    return updateScope(state, scope, currentUserScope.accessType, scopeKey, [
        ...otherBlocks,
        newBlock,
    ]);
};
